import { Collection } from 'datx';
import { first, sortBy } from 'lodash';
import { computed, observable } from 'mobx';

import { MoreMagicType } from '../enums/MoreMagicType';
import { bookPayloadFormater } from '../utils/bookPayloadFormater';
import { AddOn, Avatar, Banner, Book, Feature, Field, Magic, OptionValue, Page, Picker, Upload } from './models';
import { Font } from './models/Font';
import { Wrap } from './models/Wrap';

export class AppData extends Collection {
  public static types = [
    Magic,
    Book,
    Avatar,
    Field,
    Feature,
    Picker,
    Banner,
    OptionValue,
    Page,
    AddOn,
    Wrap,
    Upload,
    Font,
  ];

  @observable
  public orderId?: number;

  @computed
  public get price() {
    const book = first(this.findAll(Book));
    if (book) {
      return book.price;
    }

    return {};
  }

  @observable
  public bookId?: number;

  @observable
  public dedications?: Array<any> = [];

  @computed
  public get banner() {
    return first(this.findAll(Banner)) as Banner;
  }

  @computed
  public get translations() {
    const book = first(this.findAll(Book));
    if (book) {
      return book.translations;
    }

    return {};
  }

  @computed
  public get avatars() {
    return sortBy(this.findAll(Avatar), 'position');
  }

  @computed
  public get mainAvatar() {
    return this.avatars.find(({ mainCharacter }) => mainCharacter);
  }

  @computed
  public get pageImages() {
    const book = first(this.findAll(Book));
    if (book) {
      return book.pageImages;
    }

    return [];
  }

  @computed
  public get contentString() {
    let str = '';
    sortBy(this.avatars, 'id').forEach((avt) => {
      str = `${str}A${avt.id}G${avt.genderId}`;
    });

    return str;
  }

  @computed
  public get dimensions() {
    const book = first(this.findAll(Book));
    if (book) {
      return {
        height: book.height,
        width: book.width,
      };
    }

    return {};
  }

  @computed
  public get wrapDimensions() {
    const book = first(this.findAll(Book));
    if (book) {
      return {
        height: book.coverHeight,
        width: book.coverWidth,
      };
    }

    return {};
  }

  @computed
  public get configuration() {
    const book = first(this.findAll(Book));
    if (book) {
      return book.elementConfigurations;
    }

    return {};
  }

  @computed
  public get moreMagic() {
    return this.findAll(Magic);
  }

  @computed
  public get extraProducts() {
    return this.findAll(AddOn);
  }

  @computed
  public get wraps() {
    return this.findAll(Wrap);
  }

  @computed
  public get bookPayload() {
    return bookPayloadFormater(this);
  }

  @computed
  public get iconedMagic() {
    return this.moreMagic.find(({ type }) => type === MoreMagicType.ICONED);
  }

  @computed
  public get fonts() {
    return this.findAll(Font);
  }

  @computed
  public get isWideFormat() {
    // In the future this property should come from the CMS in the book options

    return Boolean(this.dimensions && (this.dimensions.width as number) > 250);
  }

  @observable
  public currentLanguage!: string;

  @observable
  public currentAppLanguage!: string;

  @observable
  public token: string | null = null;

  @observable
  public childName!: string | undefined;

  @observable
  public childGender!: string | undefined;

  @computed
  public get dedicationImage(): Upload | undefined {
    return this.findAll(Upload).find(({ type }) => type === MoreMagicType.DEDICATION) || undefined;
  }

  @computed
  public get childImage(): Upload | undefined {
    return this.findAll(Upload).find(({ type }) => type === MoreMagicType.CHILD_IMAGE) || undefined;
  }

  @computed
  public get languages() {
    const book = first(this.findAll(Book));
    if (book) {
      return book.languages;
    }

    return [];
  }
}
